import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  HiEye,
  HiCog,
  HiCheckCircle,
  HiXCircle,
  HiOfficeBuilding,
  HiPlus,
} from '../components/icons';
import { Button, Card, CardHeader, CardBody, Badge } from '../components/ui';
import { ClioConfigModal } from '../components/tenant/ClioConfigModal';
import { tenantService, type Tenant } from '../services/tenant.service';
import { showToast } from '../utils/toast';

export function TenantList() {
  const navigate = useNavigate();
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isClioModalOpen, setIsClioModalOpen] = useState(false);

  const {
    data: tenants,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantService.getAllTenants(),
    staleTime: 30000, // 30 seconds
  });

  const tenantList = tenants || [];

  const handleOpenClioModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsClioModalOpen(true);
  };

  const handleClioModalClose = () => {
    setIsClioModalOpen(false);
    setSelectedTenant(null);
    refetch(); // Refresh tenant list after modal closes
  };

  const getConnectionStatusBadge = (status: Tenant['clioConnectionStatus']) => {
    switch (status) {
      case 'connected':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <HiCheckCircle className="h-3 w-3" />
            Connected
          </Badge>
        );
      case 'expired':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <HiXCircle className="h-3 w-3" />
            Expired
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <HiXCircle className="h-3 w-3" />
            Not Connected
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-xtnd-light dark:bg-xtnd-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded w-1/4 mb-6"></div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-48 bg-xtnd-dark-200 dark:bg-xtnd-dark-700 rounded-lg"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-xtnd-light dark:bg-xtnd-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <HiXCircle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-xtnd-dark-800 dark:text-white">
              Failed to load tenants
            </h3>
            <p className="mt-1 text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
              {error instanceof Error ? error.message : 'An error occurred'}
            </p>
            <div className="mt-6">
              <Button onClick={() => refetch()} variant="primary">
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-xtnd-light dark:bg-xtnd-dark-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-xtnd-dark-800 dark:text-white flex items-center gap-3">
                <HiOfficeBuilding className="h-8 w-8 text-xtnd-blue-800 dark:text-xtnd-blue-300" />
                Tenant Management
              </h1>
              <p className="mt-2 text-xtnd-dark-600 dark:text-xtnd-dark-300">
                Manage tenant configurations and Clio integrations
              </p>
            </div>
            <Button
              variant="primary"
              leftIcon={<HiPlus />}
              onClick={() => showToast.info('Tenant creation coming soon')}
            >
              Add Tenant
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardBody className="text-center">
              <div className="text-2xl font-bold text-xtnd-blue-800 dark:text-xtnd-blue-300">{tenantList.length}</div>
              <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">Total Tenants</div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {tenantList.filter(t => t.clioConnectionStatus === 'connected').length}
              </div>
              <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                Connected to Clio
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {tenantList.filter(t => t.clioConnectionStatus === 'expired').length}
              </div>
              <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                Expired Connections
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Tenant Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tenantList.map(tenant => (
            <Card key={tenant.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white">
                    {tenant.name}
                  </h3>
                  <Badge
                    variant={tenant.status === 'active' ? 'success' : 'secondary'}
                    className="capitalize"
                  >
                    {tenant.status}
                  </Badge>
                </div>
                <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">{tenant.domain}</p>
              </CardHeader>

              <CardBody>
                <div className="space-y-4">
                  {/* Clio Connection Status */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-xtnd-dark-800 dark:text-white">
                        Clio Connection
                      </span>
                      {getConnectionStatusBadge(tenant.clioConnectionStatus)}
                    </div>

                    {tenant.lastConnected && (
                      <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                        Last connected: {formatDate(tenant.lastConnected)}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<HiEye />}
                      className="flex-1"
                      onClick={() => navigate(`/tenants/${tenant.id}`)}
                    >
                      Details
                    </Button>
                    <Button
                      variant={
                        tenant.clioConnectionStatus === 'connected' ? 'secondary' : 'primary'
                      }
                      size="sm"
                      leftIcon={<HiCog />}
                      className="flex-1"
                      onClick={() => handleOpenClioModal(tenant)}
                    >
                      {tenant.clioConnectionStatus === 'connected' ? 'Manage' : 'Connect'} Clio
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {tenantList.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <HiOfficeBuilding className="mx-auto h-12 w-12 text-xtnd-dark-300 dark:text-xtnd-dark-600" />
            <h3 className="mt-2 text-sm font-medium text-xtnd-dark-800 dark:text-white">
              No tenants found
            </h3>
            <p className="mt-1 text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
              Get started by creating your first tenant.
            </p>
            <div className="mt-6">
              <Button
                variant="primary"
                leftIcon={<HiPlus />}
                onClick={() => showToast.info('Tenant creation coming soon')}
              >
                Add Tenant
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Clio Config Modal */}
      {selectedTenant && (
        <ClioConfigModal
          open={isClioModalOpen}
          onClose={handleClioModalClose}
          tenantId={selectedTenant.id}
          tenantName={selectedTenant.name}
        />
      )}
    </div>
  );
}
